import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { ordersService } from "../../../services/orders";
import { adminOrdersService } from "../../../services/damages";
import { formatApiError } from "../../../services/api";
import { useAuth } from "../../../app/providers/AuthProvider";
import { hasPermission, Permission } from "../../../app/permissions";
import { usePersistedFilters } from "../../../hooks/usePersistedFilters";
import { Button } from "../../../components/ui/button";
import { EmptyState } from "../../../components/feedback/EmptyState";
import { ErrorState } from "../../../components/feedback/ErrorState";
import { OrdersFilters } from "../components/OrdersFilters";
import { OrdersTable, OrdersTableSkeleton } from "../components/OrdersTable";
import { NewOrdersTable } from "../components/NewOrdersTable";
import { CancelOrderModal } from "../components/CancelOrderModal";
import { ConfirmDeleteModal } from "../../../components/admin/ConfirmDeleteModal";
import { Pagination, useSessionPageSize } from "../../../components/ui/Pagination";

export default function OrdersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  // A14: warehouse also has ORDERS_VIEW_ALL. The variable name stays
  // `isAdmin` for minimal diff churn — semantically it now means
  // "cross-user viewer" (admin OR warehouse).
  const isAdmin = hasPermission(user, Permission.ORDERS_VIEW_ALL);
  // A14.3: only users who can own orders see the "Mine" tab. Warehouse
  // doesn't own orders, so this hides it for them while keeping it for admin.
  const canSeeMineTab = hasPermission(user, Permission.ORDERS_VIEW_OWN);
  // A15: warehouse-only "New Orders" tab (confirmed orders awaiting ack).
  const isWarehouseOnly = isAdmin && !canSeeMineTab;

  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statuses, setStatuses] = useState([]);
  const [fromDate, setFromDate] = useState("");
  const [untilDate, setUntilDate] = useState("");
  const [sort, setSort] = useState("created_at:desc");
  const [draftCount, setDraftCount] = useState(0);
  // A15: multiselect state for warehouse "New Orders" tab.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [ackBusyIds, setAckBusyIds] = useState(() => new Set());
  const [bulkAckRunning, setBulkAckRunning] = useState(false);

  // Tab scope.
  //   Admin:     'all' (default) | 'me' | 'drafts'
  //   Warehouse: 'new_orders' (default) | 'all'
  //   Standard:  'me' (implicit) | 'drafts'
  const defaultScope = isWarehouseOnly ? "new_orders" : (isAdmin ? "all" : "me");
  const initialScope = searchParams.get("scope") || defaultScope;
  const [scope, setScopeRaw] = useState(initialScope);

  // Admin user filter removed in Refinement 7 — the search box now
  // matches owner user first/last name via the backend, so a dedicated
  // multi-select is redundant.
  const [pageSize, setPageSize] = useSessionPageSize("orders.pageSize", 10);

  // Page is driven by the ?page= query string so refresh preserves position.
  const urlPage = Math.max(1, parseInt(searchParams.get("page") || "1", 10) || 1);
  const [page, _setPage] = useState(urlPage);
  const setPage = useCallback(
    (next) => {
      const p = typeof next === "function" ? next(page) : next;
      _setPage(p);
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (p <= 1) params.delete("page");
          else params.set("page", String(p));
          return params;
        },
        { replace: true }
      );
    },
    [page, setSearchParams]
  );

  // Keep in sync if the URL is manipulated externally.
  useEffect(() => {
    if (urlPage !== page) _setPage(urlPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlPage]);

  const changeScope = useCallback(
    (next) => {
      setScopeRaw(next);
      _setPage(1);
      setSelectedIds(new Set());
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === defaultScope) params.delete("scope");
          else params.set("scope", next);
          params.delete("page");
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams, defaultScope]
  );

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput]);

  /** Reset page → 1 when the user actively changes any filter / search. */
  const changeSearchInput = (v) => {
    setSearchInput(v);
    if (page !== 1) setPage(1);
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const showDrafts = scope === "drafts";
      const isAllScope = isAdmin && scope === "all";
      const isDraftsAdmin = isAdmin && scope === "drafts";
      const isNewOrdersScope = scope === "new_orders";
      const useAdminEndpoint = isAllScope || isDraftsAdmin || isNewOrdersScope;

      // A15: on the "New Orders" tab we pin status to `confirmed`
      // regardless of the user's filter selection — the tab IS the
      // filter. Search + sort still apply.
      const effectiveStatus = isNewOrdersScope
        ? "confirmed"
        : showDrafts
          ? "draft"
          : (statuses.length ? statuses.join(",") : undefined);

      const commonParams = {
        search: debouncedSearch || undefined,
        status: effectiveStatus,
        from_date_gte: isNewOrdersScope ? undefined : (fromDate || undefined),
        until_date_lte: isNewOrdersScope ? undefined : (untilDate || undefined),
        sort,
        page,
        page_size: pageSize,
      };

      let data;
      if (useAdminEndpoint) {
        data = await ordersService.listAll({
          ...commonParams,
          include_drafts: isDraftsAdmin ? true : undefined,
        });
      } else {
        data = await ordersService.list(commonParams);
      }
      setOrders(data.items);
      setTotal(data.total);
      // Draft count for tab badge
      if (scope !== "drafts") {
        try {
          const draftSvc = isAdmin
            ? ordersService.listAll({ status: "draft", page_size: 1, include_drafts: true })
            : ordersService.list({ status: "draft", page_size: 1 });
          const dc = await draftSvc;
          setDraftCount(dc.total);
        } catch { /* non-fatal */ }
      } else {
        setDraftCount(data.total);
      }
    } catch (err) {
      setError(formatApiError(err, t("common.error_generic")));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, statuses, fromDate, untilDate, sort, page, pageSize, scope, isAdmin, t]);

  useEffect(() => {
    load();
  }, [load]);

  // A16: persist filter state per user + route. Excludes pagination page.
  const persistedFilters = usePersistedFilters({
    route: "orders",
    userId: user?.id,
    snapshot: { statuses, fromDate, untilDate, searchInput, sort },
    apply: (snap) => {
      if (Array.isArray(snap.statuses)) setStatuses(snap.statuses);
      if (typeof snap.fromDate === "string") setFromDate(snap.fromDate);
      if (typeof snap.untilDate === "string") setUntilDate(snap.untilDate);
      if (typeof snap.searchInput === "string") {
        setSearchInput(snap.searchInput);
        setDebouncedSearch(snap.searchInput);
      }
      if (typeof snap.sort === "string") setSort(snap.sort);
    },
  });

  const isFiltered = useMemo(
    () =>
      Boolean(
        debouncedSearch || statuses.length || fromDate || untilDate
      ),
    [debouncedSearch, statuses, fromDate, untilDate]
  );

  const clearFilters = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setStatuses([]);
    setFromDate("");
    setUntilDate("");
    setPage(1);
    persistedFilters.clear();
  };

  const openCancel = (order) => setCancelTarget(order);

  // Draft-only delete flow (Bug 1). Confirmed via a dedicated modal so the
  // row's default "cancel" affordance is unavailable for drafts.
  const [deleteDraftTarget, setDeleteDraftTarget] = useState(null);
  const [deleteDraftBusy, setDeleteDraftBusy] = useState(false);
  const openDeleteDraft = (order) => setDeleteDraftTarget(order);
  const confirmDeleteDraft = async () => {
    if (!deleteDraftTarget) return;
    setDeleteDraftBusy(true);
    try {
      await ordersService.deleteDraft(deleteDraftTarget.id);
      toast.success(t("orders.delete_draft_modal.success"));
      setDeleteDraftTarget(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, t("common.error_generic")));
    } finally {
      setDeleteDraftBusy(false);
    }
  };

  const confirmCancel = async (reason) => {
    if (!cancelTarget) return;
    setCancelSubmitting(true);
    try {
      const updated = await ordersService.cancel(cancelTarget.id, reason);
      setOrders((prev) =>
        prev.map((o) => (o.id === updated.id ? updated : o))
      );
      toast.success(t("orders.cancel_modal.success"));
      setCancelTarget(null);
    } catch (err) {
      const status = err?.response?.status;
      toast.error(
        status === 409
          ? t("orders.cancel_modal.conflict")
          : formatApiError(err, t("common.error_generic"))
      );
      if (status === 409) await load();
    } finally {
      setCancelSubmitting(false);
    }
  };

  // ── A15: acknowledge (confirmed → in_progress) — per-row + bulk ──
  const currentPageIds = useMemo(() => orders.map((o) => o.id), [orders]);
  const toggleOne = (id) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      // eslint-disable-next-line no-unused-expressions
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleAllOnPage = () =>
    setSelectedIds((prev) => {
      const allSelected = currentPageIds.length > 0
        && currentPageIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) currentPageIds.forEach((id) => next.delete(id));
      else currentPageIds.forEach((id) => next.add(id));
      return next;
    });
  const clearSelection = () => setSelectedIds(new Set());

  const acknowledgeOne = async (order) => {
    setAckBusyIds((prev) => new Set(prev).add(order.id));
    try {
      await adminOrdersService.transition(order.id, { new_status: "in_progress" });
      toast.success(t("new_orders.ack_success", { order: order.order_number ? `#${order.order_number}` : "" }));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
      await load();
    } catch (err) {
      toast.error(formatApiError(err, t("common.error_generic")));
    } finally {
      setAckBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
    }
  };

  const bulkAcknowledge = async () => {
    if (selectedIds.size === 0) return;
    setBulkAckRunning(true);
    try {
      const res = await adminOrdersService.bulkAcknowledge(Array.from(selectedIds));
      toast.success(
        t("new_orders.bulk_ack_result", {
          ok: res.succeeded.length,
          skipped: res.skipped.length,
        })
      );
      clearSelection();
      await load();
    } catch (err) {
      toast.error(formatApiError(err, t("common.error_generic")));
    } finally {
      setBulkAckRunning(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const rangeStart = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const rangeEnd = Math.min(total, page * pageSize);
  const isNewOrdersScope = scope === "new_orders";

  return (
    <div className="space-y-6" data-testid="orders-page">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1
            className="text-3xl font-semibold tracking-tight text-text-primary"
            data-testid="orders-title"
          >
            {t("orders.title")}
          </h1>
          <p className="mt-2 text-sm text-text-secondary">
            {t("orders.subtitle")}
          </p>
        </div>
        <Button
          onClick={() => navigate("/orders/new")}
          data-testid="orders-new-cta"
          disabled={!hasPermission(user, Permission.ORDERS_CREATE)}
          className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne disabled:hidden"
        >
          {t("orders.new_order_cta")}
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-4 border-b border-border-subtle" data-testid="orders-tabs">
        <div className="flex items-center gap-2">
          {isWarehouseOnly && (
            <button
              type="button"
              onClick={() => changeScope("new_orders")}
              data-testid="orders-tab-new-orders"
              className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                scope === "new_orders" ? "border-champagne text-charcoal" : "border-transparent text-text-secondary hover:text-charcoal"
              }`}
            >
              {t("new_orders.tab_label")}
            </button>
          )}
          {isAdmin && canSeeMineTab && (
            <button
              type="button"
              onClick={() => changeScope("me")}
              data-testid="orders-tab-me"
              className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                scope === "me" ? "border-champagne text-charcoal" : "border-transparent text-text-secondary hover:text-charcoal"
              }`}
            >
              {t("orders.tab_me")}
            </button>
          )}
          <button
            type="button"
            onClick={() => changeScope(isAdmin ? "all" : "me")}
            data-testid="orders-tab-all"
            className={`border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
              (scope === "all" || (!isAdmin && scope === "me")) ? "border-champagne text-charcoal" : "border-transparent text-text-secondary hover:text-charcoal"
            }`}
          >
            {t("orders.tab_all")}
          </button>
          {hasPermission(user, Permission.ORDERS_CREATE) && (
            <button
              type="button"
              onClick={() => changeScope("drafts")}
              data-testid="orders-tab-drafts"
              className={`inline-flex items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${
                scope === "drafts" ? "border-champagne text-charcoal" : "border-transparent text-text-secondary hover:text-charcoal"
              }`}
          >
            {t("orders.tab_drafts")}
            {draftCount > 0 && (
              <span className="rounded-full bg-taupe/20 px-2 py-0.5 text-[10px] font-semibold text-taupe">
                {draftCount}
              </span>
            )}
          </button>
          )}
        </div>
      </div>

      {/* A17: Drafts tab strips search + all filter controls.
          Only tab / new-order affordances stay on that view. */}
      {scope !== "drafts" && (
        <OrdersFilters
          search={searchInput}
          onSearchChange={changeSearchInput}
          statuses={statuses}
          onStatusesChange={(s) => {
            setStatuses(s);
            setPage(1);
          }}
          fromDate={fromDate}
          onFromDateChange={(v) => {
            setFromDate(v);
            setPage(1);
          }}
          untilDate={untilDate}
          onUntilDateChange={(v) => {
            setUntilDate(v);
            setPage(1);
          }}
          sort={sort}
          onSortChange={(v) => {
            setSort(v);
            setPage(1);
          }}
          onClear={clearFilters}
          isFiltered={isFiltered}
        />
      )}

      {!loading && !error && (
        <div
          className="text-xs text-text-secondary"
          data-testid="orders-results-count"
        >
          {total === 1
            ? t("orders.results_count_one")
            : t("orders.results_count_other", { count: total })}
        </div>
      )}

      {isNewOrdersScope && !loading && !error && orders.length > 0 && selectedIds.size > 0 && (
        <div
          className="flex flex-wrap items-center gap-3 rounded-md border border-champagne/50 bg-champagne/10 px-4 py-2"
          data-testid="new-orders-bulk-bar"
        >
          <span className="text-sm text-text-primary">
            {t("new_orders.selected_count", { count: selectedIds.size })}
          </span>
          <Button
            size="sm"
            onClick={bulkAcknowledge}
            disabled={bulkAckRunning}
            data-testid="new-orders-bulk-ack"
            className="bg-charcoal text-white hover:bg-charcoal-soft focus-visible:ring-champagne"
          >
            {t("new_orders.bulk_ack", { count: selectedIds.size })}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={clearSelection}
            data-testid="new-orders-bulk-clear"
            className="text-taupe hover:bg-offwhite hover:text-charcoal"
          >
            {t("common.clear_filters")}
          </Button>
        </div>
      )}

      {error && !loading ? (
        <ErrorState
          title={t("orders.error_title")}
          body={error}
          onRetry={load}
          testId="orders-error"
        />
      ) : loading ? (
        <OrdersTableSkeleton />
      ) : orders.length === 0 ? (
        <EmptyState
          title={isNewOrdersScope ? t("new_orders.empty_title") : t("orders.empty_title")}
          body={
            isNewOrdersScope
              ? t("new_orders.empty_body")
              : isFiltered
              ? t("orders.empty_filtered_body")
              : t("orders.empty_body")
          }
          actionLabel={
            isNewOrdersScope
              ? null
              : isFiltered
              ? t("orders.clear_filters")
              : hasPermission(user, Permission.ORDERS_CREATE)
              ? t("orders.empty_action")
              : null
          }
          onAction={
            isNewOrdersScope
              ? null
              : isFiltered
              ? clearFilters
              : hasPermission(user, Permission.ORDERS_CREATE)
              ? () => navigate("/orders/new")
              : null
          }
          testId="orders-empty"
        />
      ) : (
        <>
          {isNewOrdersScope ? (
            <NewOrdersTable
              orders={orders}
              selectedIds={selectedIds}
              onToggleOne={toggleOne}
              onToggleAllOnPage={toggleAllOnPage}
              onAcknowledgeOne={acknowledgeOne}
              ackBusyIds={ackBusyIds}
              showUserColumn={isAdmin}
            />
          ) : (
            <OrdersTable
              orders={orders}
              onCancel={openCancel}
              onDeleteDraft={openDeleteDraft}
              showUserColumn={isAdmin && scope !== "me"}
              viewOnly={!hasPermission(user, Permission.ORDERS_CREATE)}
              hideOrderNumber={scope === "drafts"}
            />
          )}
          <Pagination
            page={page}
            totalPages={totalPages}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            total={total}
            testId="orders-pagination"
          />
        </>
      )}

      <CancelOrderModal
        open={Boolean(cancelTarget)}
        onOpenChange={(v) => !v && setCancelTarget(null)}
        onConfirm={confirmCancel}
        submitting={cancelSubmitting}
        orderNumber={cancelTarget?.order_number || ""}
      />

      <ConfirmDeleteModal
        open={Boolean(deleteDraftTarget)}
        onClose={() => setDeleteDraftTarget(null)}
        onConfirm={confirmDeleteDraft}
        title={t("orders.delete_draft_modal.title")}
        description={t("orders.delete_draft_modal.description")}
        confirmLabel={t("orders.delete_draft_modal.confirm")}
        name={deleteDraftTarget?.event_name || ""}
        busy={deleteDraftBusy}
        testId="orders-delete-draft"
      />
    </div>
  );
}
